import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { UserActions } from './index';
import { combineLatest, firstValueFrom, from, of, timer, EMPTY } from 'rxjs';
import { catchError, debounce, debounceTime, exhaustMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ApiService, USER_ROLES } from '../../services/api.service';
import { selectHmCompanyId, selectHmCompanyLogo, selectOnboardingUserRole, selectTalentHardSkills, selectTalentJobTitles, selectUserAvatar, selectUserId, selectUserRole, selectUserState, UserState } from './user.store';
import { HmUserAdapter, TalentUserAdapter, TalentUserSkillsAdapter } from './user.adapter';
import { CloudinaryUploadService } from '../../services/cloudinary-upload.service';

@Injectable()
export class UserEffects {



  getUser$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.GetUser),
    switchMap(() => this.apiService.getUser().pipe(
      map(payload => {
        if (payload && payload.roles && payload.roles.length && payload.roles.includes(USER_ROLES.talent)) {
          return UserActions.GetUserSuccess({ payload: TalentUserAdapter.toStore(payload) });  
        }
        if (payload && payload.roles && payload.roles.length && payload.roles.includes(USER_ROLES.hm)) {
          return UserActions.GetUserSuccess({ payload: HmUserAdapter.toStore(payload) });  
        }
        return [];
      }),
      catchError((err) => of(UserActions.GetUserFailure({ err })))
    ))
  ));

  setUserRole$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.SetUserRole),
    withLatestFrom(this.store.select(selectOnboardingUserRole)),
    switchMap(([ action, currentRole ]) => {
      if (action.role !== currentRole) {
        return this.apiService.updateUserRole(action.role).pipe(
          map(() => UserActions.SetUserRoleSuccess({ role: action.role })),
          catchError(() => of(UserActions.SetUserRoleSuccess({ role: action.role })))
        )
      } else {
        return of(UserActions.SetUserRoleSuccess({ role: action.role }));
      }
    })
  ));

  getJobTitles$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.GetJobTitles),
    withLatestFrom(this.store.select(selectTalentJobTitles)),
    switchMap(([ action, jobTitles ]) => {
      if (!jobTitles || !jobTitles.length) {
        return this.apiService.getJobTitles().pipe(
          map(jobTitles => UserActions.GetJobTitlesSuccess({ jobTitles })),
          catchError((err) => of(UserActions.GetJobTitlesFailure({ err })))
        )
      }
      return of();
    })
  ));

  

  getHardSkillsList$ = createEffect(() =>
  this.actions$.pipe(
    ofType(UserActions.GetSkillsList),
    switchMap(() =>
      this.apiService.getUserSkillTree().pipe(
        withLatestFrom(this.store.select(selectTalentHardSkills)),
        map(([skillTree, skills]) => {
          const branches = skillTree?.branches ?? []; // safely fallback to empty array
          return UserActions.GetSkillsListSuccess({
            skillsList: TalentUserSkillsAdapter.toViewMerge(branches, skills)
          });
        })
        // Optionally handle errors
        // catchError(() => of(UserActions.GetSkillsListSuccess({ skillsList: [] })))
      )
    )
  )
);


  setHardSkills$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.SetHardSkills),
    debounce(action => action.noDebounce ? of({}) : timer(1000)),
    tap((action) => {
      const skills = TalentUserSkillsAdapter.toServer(action.skillsList);
      if (skills) { this.apiService.setUserHardSkills(skills).subscribe(); }
      this.store.dispatch(UserActions.SetHardSkillsSuccess());
    })
  ), { dispatch: false });

  getTalentSkills$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.GetTalentSkills),
    exhaustMap(() => {
      return combineLatest([
        this.apiService.getUserHardSkills(), 
        this.apiService.getUserSoftSkills()
      ]).pipe(
        map(([ hardSkills, softSkills ]) => {
          return UserActions.GetTalentSkillsSuccess({ hardSkills, softSkills });
        }),
        catchError((err) => of(UserActions.GetTalentSkillsSuccess({ hardSkills: [], softSkills: [] })))
      );
    })
  ));

  setSoftSkills$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.SetSoftSkills),
    debounceTime(3000),
    tap((action) => {
      this.apiService.setUserSoftSkills(action.skillsList).subscribe();
    })
  ), { dispatch: false });

  getTalentSkillsTree$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.GetTalentSkillsTree),
    tap(async (action) => {
      this.store.dispatch(UserActions.GetTalentSkills());
      await firstValueFrom(this.actions$.pipe(ofType(UserActions.GetTalentSkillsSuccess)));
      this.store.dispatch(UserActions.GetSkillsList());
    })
  ), { dispatch: false });

  setTalentSeniority$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.SetTalentSeniority),
    withLatestFrom(this.store.select(selectUserState)),
    switchMap(([ action, state ]) => {
      if (action.level) {
        const payload = TalentUserAdapter.toServer({...state, 
          profile: { 
            ...state.profile, 
            details: { ...state.profile.details, jobTitleLevel: action.level } 
        }});
        return this.apiService.updateUserGeneralInfo(USER_ROLES.talent, payload).pipe(
          map(() => {
            this.store.dispatch(UserActions.GetTalentSkillsTree());
            return UserActions.SetTalentSenioritySuccess({ level: action.level });
          }),
          catchError((err) => of())
        );
      } else {
        return this.apiService.resetTalentHardSkillsPreset().pipe(
          map(() => {
            this.store.dispatch(UserActions.GetTalentSkillsTree());
            return UserActions.SetTalentSeniorityFailure();
          }),
          catchError((err) => of())
        );
      }
    })
  ));

  $saveUserProfile = createEffect(() => this.actions$.pipe(
    ofType(
      UserActions.SetUserName,
      UserActions.SetUserIncognito,
      UserActions.SetUserPublic,
      UserActions.SetTalentDetails,
      UserActions.SetTalentExpectations,
      UserActions.SetTalentContacts,
      UserActions.SetTalentTelegram,
      UserActions.SetHmAboutYou,
      UserActions.SetHmCompany,
      UserActions.SetHmFinance,
      UserActions.SetHmTelegram
    ),
    debounceTime(1000),
    withLatestFrom(this.store.select(selectUserState), this.store.select(selectUserRole)),
    tap(([action, state, role]) => {
      if (role === USER_ROLES.talent) {
        this.apiService.updateUserGeneralInfo(USER_ROLES.talent, TalentUserAdapter.toServer(state)).subscribe();
      }
      if (role === USER_ROLES.hm) {
        if (action.type === UserActions.SetHmCompany.type) {
          this.apiService.updateCompany(HmUserAdapter.companyToServer(action.company)).subscribe();
        } else {
          this.apiService.updateUserGeneralInfo(USER_ROLES.hm, HmUserAdapter.toServer(state)).subscribe();
        }
      }
      if (role === USER_ROLES.admin) {
        this.apiService.updateUserGeneralInfo(USER_ROLES.hm, { name: state?.name || null}).subscribe();
      }
      this.store.dispatch(UserActions.SaveUserProfileSuccess());
    })
  ), { dispatch: false });

  setUserAvatar$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.SetUserAvatar),
    withLatestFrom(this.store.select(selectUserId), this.store.select(selectUserAvatar)),
    switchMap(([ action, userId, currentAvatar ]) => {
      const payload = {
        bytesBase64 : action.payload.base64String,
        name: action.payload.name,
        size: action.payload.size,
        type: action.payload.type,
        currentAvatar: currentAvatar,
      }
      return this.apiService.setUserAvatar(payload).pipe(
        map(avatar => 
          UserActions.SetUserAvatarSuccess({ avatar: avatar ? avatar : null }
          )
        )
      )
      // const folder = `users/${userId}/avatar`;
      // return from(this.cloudinaryUploadService.uploadFile(action.file, folder, currentAvatar)).pipe(
      //   switchMap((avatarUrl: string) => {
      //     if (!avatarUrl) { return of(UserActions.SetUserAvatarSuccess({ avatar: null })); }
      //     return this.apiService.setUserAvatar(action.file).pipe(
      //       map(avatar => UserActions.SetUserAvatarSuccess({ avatar: avatarUrl }))
      //     )
      //   })
      // )
    })
  ));

  setCompanyLogo$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.SetCompanyLogo),
    withLatestFrom(this.store.select(selectHmCompanyId), this.store.select(selectHmCompanyLogo)),
    switchMap(([ action, companyId, currentLogo ]) => {
      const payload = {
        bytesBase64 : action.payload.base64String,
        name: action.payload.name,
        size: action.payload.size,
        type: action.payload.type,
        logo: currentLogo,
        companyId: companyId,
      }
      return this.apiService.setCompanyLogo(payload).pipe(
        map(avatar => UserActions.SetCompanyLogoSuccess({ logo: avatar ? avatar : null }))
      )
      // const folder = `companies/${companyId}/logo`;
      // return from(this.cloudinaryUploadService.uploadFile(action.file, folder, currentLogo)).pipe(
      //   switchMap((logoUrl: string) => {
      //     if (!logoUrl) { return of(UserActions.SetCompanyLogoSuccess({ logo: null })); }
      //     return this.apiService.setCompanyLogo(logoUrl, companyId).pipe(
      //       map(avatar => UserActions.SetCompanyLogoSuccess({ logo: logoUrl }))
      //     )
      //   })
      // )
    })
  ));

  uploadUserFile$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.UploadUserFile),
    switchMap((action) => {
      const payload = {
        fileUrl : "",
        bytesBase64 : action.payload.base64String,
        name: action.payload.name,
        size: action.payload.size,
        type: action.payload.type,
        details: action.payload.details,
      }
      return this.apiService.uploadFile(payload).pipe(
        map(res => {
          payload.fileUrl = res.data;
          return UserActions.UploadUserFileSuccess({ payload });
        })
      )
    })
  ));

  deleteUserFile$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.DeleteUserFile),
    switchMap((action) => {
      const resourceType = action.payload.type && (action.payload.type.includes('image/') || action.payload.type.includes('/pdf')) ? 'image' : 'raw';
      //this.cloudinaryUploadService.replaceCurrent(action.payload.publicId, resourceType);
      return this.apiService.removeFile(action.payload).pipe(
        map(res => UserActions.DeleteUserFileSuccess({ fileUrl: action.payload.fileUrl }))
      )
    })
  ));

  getSkillsValidation$ = createEffect(() => this.actions$.pipe(
    ofType(UserActions.GetSkillsValidation),
    switchMap(() => {
      return this.apiService.getSkillsValidation().pipe(
        map((res: any) => {
          return UserActions.GetSkillsValidationSuccess({ payload: res && res.data ? res.data : null });
        }),
        catchError((err) => of())
      );
    })
  ));

  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private store: Store<UserState>,
    private cloudinaryUploadService: CloudinaryUploadService
  ) { }

}
